import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toReceiveCrossdockingFromWarecloud } from '../../infra/api';
import { Trans, useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import useReceiveCrossdockingError from './useReceiveCrossdockingError';
import { Box, Chip, Typography } from '@mui/material';
import { WBoxIcon } from '@wareclouds/wareclouds-ui';

export default function useToReceiveCrossdockingFromWarecloud() {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['system']);
  const { enqueueSnackbar } = useSnackbar();

  const { showErrorMessage } = useReceiveCrossdockingError();

  return useMutation({
    mutationKey: ['receive-crossdocking-order'],
    mutationFn: toReceiveCrossdockingFromWarecloud,

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['get-to-receive-crossdocking-orders'],
        exact: false,
      });

      if (data.message === 'ORDER_PROCESS') {
        enqueueSnackbar(
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">
              <Trans
                i18nKey="SCAN_QR_CROSSDOCKING_SUCCESS"
                ns="system"
                values={{
                  code: data.data.order_code,
                  progress: `${data.data.packages_proccesed} de ${data.data.total_packages}`,
                }}
                components={{
                  pill: (
                    <Chip
                      component="div"
                      sx={{
                        color: (theme) => theme.palette.background.paper,
                        fontWeight: 'bold',
                      }}
                      size="small"
                      label={`${data.data.packages_proccesed} de ${data.data.total_packages}`}
                      variant="outlined"
                      icon={<WBoxIcon sx={{ color: '#ffffff !important' }} />}
                    />
                  ),
                }}
              />
            </Typography>
          </Box>,
          { variant: 'success', autoHideDuration: 8000 }
        );
      } else {
        enqueueSnackbar(
          t('SCAN_QR_SUCCESS', {
            ns: 'system',
            code: data?.data?.order_code || variables.scanValue,
          }),
          { variant: 'success', autoHideDuration: 8000 }
        );
      }

      if (data.data?.id_route) {
        enqueueSnackbar(
          <Trans
            i18nKey="CROSSDOCKING_ROUTE_ASSIGNED_SUCCESS"
            ns="system"
            values={{ routeId: data.data.id_route }}
            components={{ strong: <strong /> }}
          />,
          {
            variant: 'info',
            autoHideDuration: 8000,
          }
        );
      } else {
        enqueueSnackbar(
          t('ROUTE_NOT_ASSIGNED_SUCCESS', {
            ns: 'system',
          }),
          { variant: 'info', autoHideDuration: 8000 }
        );
      }
    },

    onError: (error, variables) => {
      showErrorMessage(error, variables);
    },
  });
}
